/**
 * Sets the value of a property or nested property within an object based on a dot
 * delimited path.
 *
 * Example:
 *
 * ```javascript
 * const obj = {
 *    information: {
 *       address: {
 *          country: 'United States'
 *       }
 *    }
 * }
 *
 * const path = 'information.address.country'
 *
 * const result = getPropertyValueByPath(obj, path, 'Canada');
 * ```
 *
 * The value of `result` would be:
 *
 * ```typescript
 * {
 *    information: {
 *       address: {
 *          country: 'Canada'
 *       }
 *    }
 * }
 * ```
 */
export function setPropertyValueByPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const propertyPath: Array<string> = path.split('.');
  let currentPropertyRef: unknown = obj;

  // Loop through all properties EXCEPT the last property in the path. Each property except
  // the last should be an object or array. The last property is the one which should have its value set.
  for (const propertyName of propertyPath.slice(0, -1)) {
    const current: Record<string, unknown> = currentPropertyRef as Record<string, unknown>;
    // If the current part of the path is null or undefined, set it to an empty object.
    if (current[propertyName] === null || current[propertyName] === undefined) {
      current[propertyName] = {};
    }

    currentPropertyRef = current[propertyName];
  }

  // Once the property path has traversed to the second-to-last part of the path, set the last part of the
  // path to the provided value.
  (currentPropertyRef as Record<string, unknown>)[propertyPath.slice(-1)[0]] = value;

  return obj;
}
